// ════════════════════════════════════════════════════════════════════
// Glass-i — Stage SALES-RECEIPTS-2 · pure helpers for allocating an on-account
// advance across confirmed proformas. No React, no network — unit-testable.
// A "rows" map is { [proformaId]: amount }.
// ════════════════════════════════════════════════════════════════════

function n2(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

// Sum of an allocation map (ignores blanks / non-numbers).
export function sumAllocations(rows) {
  if (!rows || typeof rows !== 'object') return 0;
  return n2(Object.values(rows).reduce((s, v) => s + n2(v), 0));
}

// How much of the receipt is still unallocated (never negative-rounded away).
export function allocationRemaining(receiptAmount, rows) {
  return n2(n2(receiptAmount) - sumAllocations(rows));
}

// FIFO auto-fill: pour the receipt into the proformas oldest-first, each up to
// its grand total, until the receipt is exhausted. `proformas` is the ordered
// list [{ id, grandTotal }]. Returns a fresh { [id]: amount } map (positive only).
export function buildFifoAllocation(receiptAmount, proformas) {
  let left = n2(receiptAmount);
  const out = {};
  for (const pi of Array.isArray(proformas) ? proformas : []) {
    if (left <= 0) break;
    if (!pi || !pi.id) continue;
    const take = Math.min(left, n2(pi.grandTotal));
    if (take > 0) { out[pi.id] = take; left = n2(left - take); }
  }
  return out;
}

// Validate an allocation map against the receipt. Returns { ok, error }.
export function validateAllocations(receiptAmount, rows) {
  const amt = n2(receiptAmount);
  const map = rows && typeof rows === 'object' ? rows : {};
  for (const [, v] of Object.entries(map)) {
    if (v == null || v === '') continue;
    if (!Number.isFinite(Number(v)) || Number(v) < 0) return { ok: false, error: 'Amounts must be zero or more.' };
  }
  if (sumAllocations(map) - amt > 0.001) return { ok: false, error: 'Allocations exceed the receipt amount.' };
  return { ok: true, error: null };
}

// Convert an allocation map → the API payload array (positive lines only).
export function allocationsToPayload(rows) {
  const map = rows && typeof rows === 'object' ? rows : {};
  return Object.entries(map)
    .map(([proformaId, amount]) => ({ proformaId, amount: n2(amount) }))
    .filter((l) => l.amount > 0);
}
